import React, { useState, useEffect, useCallback } from 'react';
import './Compare.css';
import { Helmet } from 'react-helmet';


const API_BASE = 'https://pcbuildguru-api.onrender.com'; // Adjust this to your backend API base URL

const Compare = () => {
  const [componentType, setComponentType] = useState('');
  const [componentList, setComponentList] = useState([]);
  const [selectedItems, setSelectedItems] = useState([null, null]);
  const [comparisonData, setComparisonData] = useState({ headers: [], rows: [] });
  // FIX 1: isLoading state and setters are commented out to remove the 'no-unused-vars' warning.
  // const [isLoading, setIsLoading] = useState(false);

  // FIX 3: Wrapped renderNestedTable in useCallback to give it a stable reference.
  const renderNestedTable = useCallback((obj) => {
    if (obj == null) return 'N/A';
    if (Array.isArray(obj)) return obj.join(', ');
    if (typeof obj !== 'object') return String(obj);

    return (
      <table className="nested-table">
        <tbody>
          {Object.entries(obj).map(([key, value]) => (
            <tr key={key}>
              <td className="nested-attr">
                {key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()}
              </td>
              <td className="nested-value">{renderNestedTable(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }, []);


  // Fetch only name + price when type changes
  useEffect(() => {
    if (!componentType) {
      setComponentList([]);
      setSelectedItems([null, null]);
      return;
    }
    
    // setIsLoading(true); // Commented out
    fetch(`${API_BASE}/api/components?type=${componentType}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch components');
        return res.json();
      })
      .then(data => {
        setComponentList(data);
        setSelectedItems([null, null]);
      })
      .catch(err => console.error('Error fetching component list:', err))
      // .finally(() => setIsLoading(false)); // Commented out
  }, [componentType]);

  // Fetch full data whenever selections change
  useEffect(() => {
    const selectedIds = selectedItems.filter(Boolean).map(item => item.id);
    if (selectedIds.length === 0) {
      setComparisonData({ headers: [], rows: [] });
      return;
    }

    // setIsLoading(true); // Commented out
    fetch(`${API_BASE}/api/component-details`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        type: componentType,
        ids: selectedIds 
      })
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch component details');
        return res.json();
      })
      .then(data => {
        console.log("API Response:", data);
        const headers = data.map(item => 
          item.Name || item.name || item.Processor || 'Unnamed'
        );

        const allKeys = new Set();
        data.forEach(item => 
          Object.keys(item).forEach(k => allKeys.add(k))
        );
        
        // FIX 2: The unused `formatValue` function has been removed.

        const rows = Array.from(allKeys)
          .filter(k => k !== 'id')
          .map(k => ({
            attribute: k
              .replace(/_/g, ' ')
              .replace(/([A-Z])/g, ' $1')
              .trim(),
            values: data.map(item => {
              if (!item || item[k] === undefined || item[k] === null) return 'N/A';
              return renderNestedTable(item[k]);
            })
          }));

        setComparisonData({ headers, rows });
      })
      .catch(err => {
        console.error('Error fetching details:', err);
        setComparisonData({ headers: [], rows: [] });
      })
      // .finally(() => setIsLoading(false)); // Commented out
  // FIX 3: Added `renderNestedTable` to the dependency array to satisfy the linter.
  }, [selectedItems, componentType, renderNestedTable]);

  const handleComponentTypeChange = (e) => {
    setComponentType(e.target.value);
  };

  const handleItemSelect = (index, itemId) => {
    const item = componentList.find(c => c.id === itemId) || null;
    const newSelectedItems = [...selectedItems];
    newSelectedItems[index] = item;
    setSelectedItems(newSelectedItems);
  };

  const addSelector = () => {
    if (selectedItems.length < 4) {
      setSelectedItems([...selectedItems, null]);
    }
  };

  const removeSelector = (index) => {
    const newSelectedItems = [...selectedItems];
    newSelectedItems.splice(index, 1);
    setSelectedItems(newSelectedItems);
  };

  return (
    <div className="compare-page-container">
      <Helmet>
        <title>Compare PC Parts – CPUs, GPUs, RAM, SSDs & More | PC Build Guru</title>
        <meta
          name="description"
          content="Side-by-side comparison of PC components including CPUs, GPUs, motherboards, RAM, storage, and more. Choose up to 4 parts and compare features, specs, and prices."
        />
        <meta
          name="keywords"
          content="compare pc parts, cpu comparison, gpu comparison, pc hardware comparison, gaming PC parts, build a pc comparison"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.pcbuildguru.com/compare" />

        {/* Open Graph */}
        <meta property="og:title" content="Compare PC Parts – CPUs, GPUs, RAM, SSDs & More" />
        <meta
          property="og:description"
          content="Compare up to 4 PC components side-by-side. Check detailed specs, features, and prices before making your build decision."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.pcbuildguru.com/compare" />
        <meta property="og:site_name" content="PC Build Guru" />
        <meta property="og:image" content="https://www.pcbuildguru.com/images/compare-og-image.jpg" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Compare PC Parts – CPUs, GPUs, RAM, SSDs & More" />
        <meta
          name="twitter:description"
          content="Compare PC components side-by-side to make the best choice for your build."
        />
        <meta name="twitter:image" content="https://www.pcbuildguru.com/images/compare-twitter-image.jpg" />

        {/* Structured Data for Product Comparison */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "PC Component Comparison",
            "description":
              "Interactive PC parts comparison for CPUs, GPUs, RAM, storage, and more.",
            "itemListElement": componentList.slice(0, 4).map((item, index) => ({
              "@type": "Product",
              "position": index + 1,
              "name": item?.name || "",
              "offers": {
                "@type": "Offer",
                "price": item?.price || 0,
                "priceCurrency": "INR"
              }
            }))
          })}
        </script>
      </Helmet>

      <header className="compare-header">
        <h1>Compare PC Components</h1>
        <p>
          Select a component category and choose up to four PC parts for an
          instant, detailed comparison of their specifications, features,
          and prices.
        </p>
      </header>


      <div className="compare-controls">
        <div className="control-group">
          <label htmlFor="componentType">Component Type</label>
          <select id="componentType" value={componentType} onChange={handleComponentTypeChange}>
            <option value="">-- Select a Category --</option>
            <option value="cpu">CPU</option>
            <option value="motherboard">Motherboard</option>
            <option value="gpu">Graphics Card (GPU)</option>
            <option value="ram">RAM</option>
            <option value="ssd">SSD</option>
            <option value="hdd">HDD</option>
            <option value="psu">Power Supply (PSU)</option>
            <option value="cooler">CPU Cooler</option>
            <option value="cabinet">Cabinet (Case)</option>
          </select>
        </div>

        <div className="item-selectors">
          {selectedItems.map((item, index) => (
            <div key={index} className="item-selector-wrapper">
              <select
                value={item ? item.id : ''}
                onChange={(e) => handleItemSelect(index, e.target.value)}
                disabled={!componentType}
              >
                <option value="">-- Select Item {index + 1} --</option>
                {componentList.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.name} (₹{(option.price || 0).toLocaleString('en-IN')})
                  </option>
                ))}
              </select>
              {index > 1 && (
                <button onClick={() => removeSelector(index)} className="remove-button">&times;</button>
              )}
            </div>
          ))}
          {selectedItems.length < 4 && (
            <button onClick={addSelector} className="add-button" disabled={!componentType}>+</button>
          )}
        </div>
      </div>

      {comparisonData.rows.length > 0 ? (
        <div className="comparison-table-container">
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Feature</th>
                {comparisonData.headers.map((header, index) => (
                  <th key={index}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonData.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td>{row.attribute}</td>
                  {row.values.map((value, colIndex) => (
                    <td key={colIndex}>{value}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : selectedItems.filter(Boolean).length > 0 ? (
        <div className="no-data-message">
          <p>No comparison data available for the selected components.</p>
        </div>
      ) : null}
    </div>
  );
};

export default Compare;